/*
 * Community Videos — spotlight carousel.
 * Performance model:
 *   - Nothing here touches page load: Swiper (lib + css) is fetched only when
 *     the section approaches the viewport, and the carousel is built then too.
 *   - Only the centered slide ever holds a <video>; swiping away frees it, so
 *     memory and bandwidth stay flat regardless of how many videos exist.
 *   - The centered video buffers a muted preview while visible, so pressing
 *     play starts instantly. The poster covers any decode/seek so there is no
 *     black flash.
 */
(function () {
  if (window.__ugcSpotlightInit) return;
  window.__ugcSpotlightInit = true;

  // ---- Swiper lazy loader (shared across sections, fetched once) ----
  function loadSwiper(root, cb) {
    if (window.Swiper) {
      cb();
      return;
    }
    if (window.__ugcSwiperQueue) {
      window.__ugcSwiperQueue.push(cb);
      return;
    }
    window.__ugcSwiperQueue = [cb];

    var cssUrl = root.getAttribute('data-ugc-swiper-css');
    if (cssUrl && !document.querySelector('link[data-ugc-swiper-css]')) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.setAttribute('data-ugc-swiper-css', '');
      document.head.appendChild(link);
    }

    var script = document.createElement('script');
    script.src = root.getAttribute('data-ugc-swiper-js');
    script.async = true;
    script.onload = function () {
      var queue = window.__ugcSwiperQueue || [];
      window.__ugcSwiperQueue = null;
      queue.forEach(function (fn) {
        fn();
      });
    };
    document.head.appendChild(script);
  }

  // ---- Helpers ----
  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    var total = Math.floor(seconds);
    var mins = Math.floor(total / 60);
    var secs = total % 60;
    return mins + ':' + String(secs).padStart(2, '0');
  }

  function getMedia(slide) {
    return slide && slide.querySelector('[data-ugc-media]');
  }

  // Free a slide's video so it stops consuming memory/bandwidth off-center.
  function resetMedia(media) {
    if (!media) return;
    var video = media.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
    }
    media.classList.remove('is-playing', 'is-paused', 'has-started', 'is-frame-ready');
    delete media.__ugcPreviewing;
    delete media.__ugcPreviewRetryBound;
    var controls = media.querySelector('[data-ugc-controls]');
    if (controls) controls.hidden = true;
    var scrub = media.querySelector('[data-ugc-scrub]');
    if (scrub) {
      scrub.value = '0';
      scrub.__ugcDragging = false;
    }
    media.style.removeProperty('--ugc-scrub-pct');
    var current = media.querySelector('[data-ugc-time-current]');
    if (current) current.textContent = '0:00';
    var toggleBtn = media.querySelector('[data-ugc-toggle]');
    if (toggleBtn) toggleBtn.setAttribute('aria-label', 'Pause video');
  }

  function updateControlsVisibility(media) {
    var controls = media && media.querySelector('[data-ugc-controls]');
    if (!controls) return;
    controls.hidden = !media.classList.contains('has-started');
  }

  function setPlayingState(media, playing) {
    if (!media) return;
    media.classList.toggle('is-playing', playing);
    media.classList.toggle('is-paused', !playing);
    var toggleBtn = media.querySelector('[data-ugc-toggle]');
    if (toggleBtn) toggleBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    updateControlsVisibility(media);
  }

  function getActiveSlideEl(swiper) {
    return swiper.slides[swiper.activeIndex];
  }

  function unloadInactiveSlides(root, activeSlide) {
    root.querySelectorAll('.swiper-slide').forEach(function (slide) {
      if (slide !== activeSlide) resetMedia(getMedia(slide));
    });
  }

  function unloadAllVideos(root) {
    root.querySelectorAll('[data-ugc-media]').forEach(resetMedia);
  }

  // Hide poster / show video only after a painted frame (preview + play share this).
  function revealOnFrame(video, media) {
    var done = function () {
      media.classList.add('is-frame-ready');
    };

    function waitForPaint() {
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(done);
        return;
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(done);
      });
    }

    if (!video.paused && video.readyState >= 2) {
      waitForPaint();
      return;
    }

    video.addEventListener(
      'playing',
      function () {
        waitForPaint();
      },
      { once: true }
    );
  }

  function bindControls(video, media) {
    if (video.__ugcControlsBound) return;
    video.__ugcControlsBound = true;

    var scrub = media.querySelector('[data-ugc-scrub]');
    var current = media.querySelector('[data-ugc-time-current]');
    var total = media.querySelector('[data-ugc-time-total]');

    function syncScrub() {
      if (!video.duration) return;
      var pct = (video.currentTime / video.duration) * 100 || 0;
      if (scrub && !scrub.__ugcDragging) {
        scrub.value = String(pct);
        media.style.setProperty('--ugc-scrub-pct', pct + '%');
      }
      if (current) current.textContent = formatTime(video.currentTime);
      if (total) total.textContent = formatTime(video.duration);
    }

    video.addEventListener('loadedmetadata', syncScrub);
    video.addEventListener('timeupdate', syncScrub);

    if (scrub) {
      scrub.addEventListener('pointerdown', function (event) {
        event.stopPropagation();
        scrub.__ugcDragging = true;
      });
      scrub.addEventListener('pointerup', function () {
        scrub.__ugcDragging = false;
      });
      scrub.addEventListener('input', function (event) {
        event.stopPropagation();
        if (!video.duration) return;
        var pct = parseFloat(scrub.value);
        video.currentTime = (pct / 100) * video.duration;
        media.style.setProperty('--ugc-scrub-pct', pct + '%');
        syncScrub();
      });
    }

    var controls = media.querySelector('[data-ugc-controls]');
    if (controls) {
      controls.addEventListener('pointerdown', function (event) {
        event.stopPropagation();
      });
      controls.addEventListener('click', function (event) {
        event.stopPropagation();
      });
    }

    var toggleBtn = media.querySelector('[data-ugc-toggle]');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (video.paused) {
          video.play().catch(function () {});
        } else {
          video.pause();
        }
      });
    }
  }

  function ensureVideo(slide) {
    var media = getMedia(slide);
    if (!media) return null;

    var existing = media.querySelector('video');
    if (existing) return existing;

    var mp4 = slide.getAttribute('data-video-src');
    if (!mp4) return null;

    var video = document.createElement('video');
    video.className = 'ugc-video__embed';
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('preload', 'auto');
    video.loop = true;
    video.src = mp4;
    media.appendChild(video);
    video.load();

    bindControls(video, media);

    video.addEventListener('play', function () {
      if (media.classList.contains('has-started')) setPlayingState(media, true);
    });
    video.addEventListener('pause', function () {
      if (media.classList.contains('has-started')) setPlayingState(media, false);
    });
    video.addEventListener('ended', function () {
      if (media.classList.contains('has-started')) setPlayingState(media, false);
    });

    return video;
  }

  function bindPreviewRetry(video, media, root) {
    if (media.__ugcPreviewRetryBound) return;
    media.__ugcPreviewRetryBound = true;

    function retry() {
      if (media.classList.contains('has-started')) return;
      if (root && root.getAttribute('data-in-view') !== 'true') return;
      delete media.__ugcPreviewing;
      previewSlide(root, media.closest('.swiper-slide'));
    }

    document.addEventListener('touchstart', retry, { once: true, passive: true });
    document.addEventListener('pointerdown', retry, { once: true, passive: true });
    document.addEventListener(
      'visibilitychange',
      function () {
        if (!document.hidden) retry();
      },
      { passive: true }
    );
  }

  function startMutedPreview(video, media, root) {
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    if (!media.classList.contains('has-started')) {
      video.loop = true;
    }

    var attempts = 0;

    function attemptPlay() {
      attempts += 1;
      var playback = video.play();
      if (playback && playback.then) {
        playback
          .then(function () {
            revealOnFrame(video, media);
          })
          .catch(function () {
            if (attempts < 5) {
              window.setTimeout(attemptPlay, 180 * attempts);
              return;
            }
            bindPreviewRetry(video, media, root);
          });
        return;
      }
      revealOnFrame(video, media);
    }

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      attemptPlay();
      return;
    }

    video.addEventListener('loadeddata', attemptPlay, { once: true });
    video.addEventListener(
      'canplay',
      function () {
        if (media.__ugcPreviewing && video.paused) attemptPlay();
      },
      { once: true }
    );
  }

  // Muted, autoplaying preview of the centered slide — this is what buffers the
  // file ahead of a click so playback starts instantly.
  function previewSlide(root, slide) {
    if (!slide || !root) return;
    var media = getMedia(slide);
    if (!media || media.classList.contains('has-started')) return;

    var video = media.querySelector('video') || ensureVideo(slide);
    if (!video) return;

    if (media.__ugcPreviewing) {
      if (!video.paused || media.classList.contains('is-frame-ready')) return;
    }

    unloadInactiveSlides(root, slide);
    media.__ugcPreviewing = true;
    startMutedPreview(video, media, root);
  }

  // Full playback from the start, with sound, when the user presses play.
  function playSlide(root, slide) {
    if (!slide || !root) return;
    var media = getMedia(slide);
    if (!media) return;

    unloadInactiveSlides(root, slide);
    var video = ensureVideo(slide);
    if (!video) return;

    delete media.__ugcPreviewing;
    media.classList.add('has-started');
    video.loop = false;
    media.style.setProperty('--ugc-scrub-pct', '0%');

    var scrub = media.querySelector('[data-ugc-scrub]');
    if (scrub) scrub.value = '0';
    var current = media.querySelector('[data-ugc-time-current]');
    if (current) current.textContent = '0:00';
    updateControlsVisibility(media);

    function startAudible() {
      video.muted = false;
      var playback = video.play();
      if (playback && playback.catch) {
        playback.catch(function () {
          video.muted = true;
          video.play().catch(function () {});
        });
      }
      revealOnFrame(video, media);
    }

    // Preview already at t≈0 with a painted frame — unmute in place (no re-cover).
    if (media.classList.contains('is-frame-ready') && video.currentTime <= 0.15) {
      video.muted = false;
      var quickPlay = video.play();
      if (quickPlay && quickPlay.catch) {
        quickPlay.catch(function () {
          video.muted = true;
          video.play().catch(function () {});
        });
      }
      return;
    }

    video.pause();
    media.classList.remove('is-frame-ready');

    if (video.currentTime <= 0.15) {
      startAudible();
      return;
    }

    video.addEventListener('seeked', startAudible, { once: true });
    video.currentTime = 0;
  }

  function previewActiveSlide(root, swiper) {
    if (!root || root.getAttribute('data-in-view') !== 'true') return;
    var slide = getActiveSlideEl(swiper);
    previewSlide(root, slide);
    observeActiveMedia(root, swiper, slide);
  }

  function observeActiveMedia(root, swiper, slide) {
    var media = getMedia(slide || getActiveSlideEl(swiper));
    if (!media) return;

    if (root.__ugcMediaIO) {
      root.__ugcMediaIO.disconnect();
    }

    root.__ugcMediaIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.45) return;
          previewSlide(root, media.closest('.swiper-slide'));
        });
      },
      { threshold: [0, 0.25, 0.45, 0.65, 1] }
    );
    root.__ugcMediaIO.observe(media);
  }

  // ---- Pagination (one dot per real video, driven by data-slide-index) ----
  function nearestSlideForVideo(swiper, videoIdx, anchor) {
    var slides = swiper.slides;
    var best = -1;
    var bestDist = Infinity;
    for (var i = 0; i < slides.length; i++) {
      if (parseInt(slides[i].getAttribute('data-slide-index'), 10) === videoIdx) {
        var dist = Math.abs(i - anchor);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
    }
    return best;
  }

  function goToVideo(swiper, videoIdx) {
    var best = nearestSlideForVideo(swiper, videoIdx, swiper.activeIndex);
    if (best >= 0) swiper.slideTo(best);
  }

  // Swiper's loop runs its own loopFix on init/update which can park the active
  // slide at the boundary (index 0), leaving one side blank. Re-center on the
  // middle copy of video 1 so it sits with a full buffer both sides — no gaps.
  function centerOnVideo(swiper, videoIdx) {
    var mid = Math.floor(swiper.slides.length / 2);
    var best = nearestSlideForVideo(swiper, videoIdx, mid);
    if (best >= 0) swiper.slideTo(best, 0, false);
  }

  function currentVideoIndex(swiper) {
    var active = swiper.slides[swiper.activeIndex];
    if (active) {
      var di = active.getAttribute('data-slide-index');
      if (di !== null && di !== '') return parseInt(di, 10);
    }
    var count = swiper.__ugcVideoCount || 1;
    return ((swiper.realIndex % count) + count) % count;
  }

  function updatePaginationState(swiper) {
    if (!swiper.__ugcDots) return;
    var cur = currentVideoIndex(swiper);
    swiper.__ugcDots.forEach(function (dot, i) {
      var on = i === cur;
      dot.classList.toggle('is-active', on);
      if (on) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function buildPagination(root, swiper, count) {
    var pagination = root.querySelector('.ugc-video__pagination');
    if (!pagination || count < 2) return;
    pagination.innerHTML = '';
    var dots = [];
    for (var i = 0; i < count; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ugc-video__dot';
      dot.setAttribute('aria-label', 'Go to video ' + (i + 1));
      (function (idx) {
        dot.addEventListener('click', function () {
          goToVideo(swiper, idx);
        });
      })(i);
      pagination.appendChild(dot);
      dots.push(dot);
    }
    swiper.__ugcDots = dots;
    swiper.__ugcVideoCount = count;
    updatePaginationState(swiper);
  }

  function initSwiper(root) {
    var container = root.querySelector('[data-ugc-swiper]');
    if (!container || container.swiper) return container && container.swiper;

    var carousel = root.querySelector('.ugc-video__carousel');
    var prevEl = carousel && carousel.querySelector('.ugc-video__nav--prev');
    var nextEl = carousel && carousel.querySelector('.ugc-video__nav--next');
    var wrapper = container.querySelector('.swiper-wrapper');
    var originalSlides = [].slice.call(wrapper.children);
    var originalCount = originalSlides.length;
    var carouselLoop = root.getAttribute('data-carousel-loop') !== 'false' && originalCount > 1;

    // Swiper 11's loop relocates real slides (no DOM clones), so a centered loop
    // with only ~8 videos can never fill both sides at once — one edge goes
    // blank. Tripling the set gives a generous buffer everywhere; starting on
    // the middle copy of the first video centers video 1 with its real
    // neighbours already showing. Skipped in the editor (avoids dup block ids).
    var isDesignMode = !!(window.Shopify && window.Shopify.designMode);
    var initialSlide = 0;
    if (carouselLoop && wrapper && !isDesignMode) {
      for (var copy = 0; copy < 2; copy++) {
        originalSlides.forEach(function (el) {
          var clone = el.cloneNode(true);
          [].slice.call(clone.attributes).forEach(function (attr) {
            if (attr.name.indexOf('data-shopify') === 0 || attr.name === 'id') {
              clone.removeAttribute(attr.name);
            }
          });
          clone.setAttribute('data-ugc-clone', 'true');
          wrapper.appendChild(clone);
        });
      }
      initialSlide = originalCount;
    }

    /* eslint-disable no-undef */
    var swiper = new Swiper(container, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 14,
      slidesPerGroup: 1,
      grabCursor: true,
      watchOverflow: false,
      threshold: 8,
      speed: 450,
      loop: carouselLoop,
      loopAdditionalSlides: 2,
      initialSlide: initialSlide,
      breakpoints: {
        750: { spaceBetween: 16 },
        990: { spaceBetween: 18 },
        1200: { spaceBetween: 20 },
      },
      navigation: prevEl && nextEl ? { prevEl: prevEl, nextEl: nextEl } : false,
      pagination: false,
      on: {
        init: function () {
          root.setAttribute('data-ugc-ready', 'true');
          requestAnimationFrame(function () {
            swiper.update();
            previewActiveSlide(root, swiper);
          });
        },
        slideChange: function () {
          var sw = this;
          updatePaginationState(sw);
          unloadInactiveSlides(root, getActiveSlideEl(sw));
          // Injecting video during the swipe transform causes a black flash on
          // mobile — wait for the slide to settle (fallback when loop skips anim).
          if (!sw.animating) {
            previewActiveSlide(root, sw);
          }
        },
        slideChangeTransitionEnd: function () {
          previewActiveSlide(root, this);
        },
      },
    });
    /* eslint-enable no-undef */

    buildPagination(root, swiper, originalCount);

    container.swiper = swiper;
    return swiper;
  }

  function setInView(root, inView) {
    root.setAttribute('data-in-view', inView ? 'true' : 'false');
    if (!inView) {
      unloadAllVideos(root);
      return;
    }
    var container = root.querySelector('[data-ugc-swiper]');
    if (!container || !container.swiper) return;
    var swiper = container.swiper;

    // Swiper may have inited just below the fold; recompute sizes once, then
    // deterministically center video 1 (its own loopFix otherwise parks the
    // active slide at the boundary, leaving one side blank).
    if (!root.__ugcCentered) {
      root.__ugcCentered = true;
      swiper.update();
      if (swiper.params.loop) {
        centerOnVideo(swiper, 0);
        updatePaginationState(swiper);
      }
    }
    previewActiveSlide(root, swiper);
  }

  function applyInView(root, visible) {
    if (root.__ugcInView === visible) return;
    root.__ugcInView = visible;
    setInView(root, visible);
  }

  // Single source of truth for "is the block near / on screen". Nothing loads
  // until the section is within ~600px (page load stays free); the centered
  // preview plays only while actually visible and frees itself when it leaves.
  function evaluate(root) {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var rect = root.getBoundingClientRect();
    if (rect.bottom < -600 || rect.top > vh + 600) {
      applyInView(root, false);
      return;
    }
    loadSwiper(root, function () {
      initSwiper(root);
      var r = root.getBoundingClientRect();
      applyInView(root, r.bottom > 0 && r.top < vh);
    });
  }

  function observeRoot(root) {
    if (root.__ugcObserved) return;
    root.__ugcObserved = true;

    if (window.Shopify && window.Shopify.designMode) {
      loadSwiper(root, function () {
        initSwiper(root);
        applyInView(root, true);
      });
      return;
    }

    // IntersectionObserver is the efficient primary trigger; a rAF-throttled
    // scroll/resize fallback guarantees the block still initialises if an
    // observer registered during a background/bfcache load fails to deliver.
    var scheduled = false;
    var onScroll = function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        evaluate(root);
      });
    };
    root.__ugcOnScroll = onScroll;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.target !== root) return;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
              loadSwiper(root, function () {
                initSwiper(root);
                applyInView(root, true);
              });
              return;
            }
            if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
              applyInView(root, false);
            }
          });
        },
        { rootMargin: '600px 0px', threshold: [0, 0.2, 0.5] }
      );
      io.observe(root);
      root.__ugcIO = io;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    evaluate(root);
  }

  function teardownRoot(root) {
    unloadAllVideos(root);
    if (root.__ugcIO) {
      root.__ugcIO.disconnect();
      delete root.__ugcIO;
    }
    if (root.__ugcOnScroll) {
      window.removeEventListener('scroll', root.__ugcOnScroll);
      window.removeEventListener('resize', root.__ugcOnScroll);
      delete root.__ugcOnScroll;
    }
    if (root.__ugcMediaIO) {
      root.__ugcMediaIO.disconnect();
      delete root.__ugcMediaIO;
    }
    root.querySelectorAll('[data-ugc-swiper]').forEach(function (container) {
      if (container.swiper) {
        container.swiper.destroy(true, true);
        delete container.swiper;
      }
    });
    root.removeAttribute('data-ugc-ready');
    root.removeAttribute('data-in-view');
    delete root.__ugcObserved;
    delete root.__ugcCentered;
    delete root.__ugcInView;
  }

  // ---- Play / pause via tap on the centered slide ----
  document.addEventListener('click', function (event) {
    var playBtn = event.target.closest('[data-ugc-play]');
    if (playBtn) {
      var playRoot = playBtn.closest('[data-ugc-root]');
      var playSlideEl = playBtn.closest('.swiper-slide');
      if (!playRoot || !playSlideEl || !playSlideEl.classList.contains('swiper-slide-active')) return;
      event.preventDefault();
      playSlide(playRoot, playSlideEl);
      return;
    }

    var media = event.target.closest('[data-ugc-media]');
    if (!media || !media.classList.contains('has-started')) return;
    if (event.target.closest('[data-ugc-controls]')) return;

    var slide = media.closest('.swiper-slide');
    if (!slide || !slide.classList.contains('swiper-slide-active')) return;

    var video = media.querySelector('video');
    if (!video) return;

    event.preventDefault();
    if (video.paused) {
      video.play().catch(function () {});
    } else {
      video.pause();
    }
  });

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-ugc-root]').forEach(observeRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-ugc-root]');
    if (root) teardownRoot(root);
  });
})();
